import type { ActionRequest, ActionResult, Page, Values } from '@fieldia/core';
import type { Tokens } from '@fieldia/viewer';

/**
 * The record head lane's own page (`?page=wi-head`, and over itself in a
 * large popup with `&open=dialog`): a bug drawn with a work item's head — its
 * kind's strip and badge, the number before the title, the row with who it is
 * assigned to and "1 Comment", Save and Close with Save in its menu, Follow
 * with its gear, Refresh, Undo and ⋮, the band of State, Reason, Area and
 * Iteration with their access keys, "Updated by", and the tabs Details,
 * History, Links and Attachments in the head. Every record and name is
 * invented. Its body is plain: the groups' own look is another lane's.
 */

const STATES = [
  { value: 'new', label: 'New' },
  { value: 'active', label: 'Active' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export const wiHeadPage: Page = {
  fieldia: '0.1',
  id: 'wi-head',
  title: 'Bug',
  data: { kind: 'record', model: 'wi.head' },
  look: { theme: 'fluent', skin: 'quiet', density: 'compact', lockAt: 'value' },
  fields: {
    number: { type: 'integer', label: 'ID', readonly: true },
    work_type: { type: 'selection', label: 'Work Item Type', options: [{ value: 'bug', label: 'Bug' }, { value: 'task', label: 'Task' }, { value: 'story', label: 'User Story' }] },
    title: { type: 'char', label: 'Title', required: true },
    assigned_to: { type: 'many2one', label: 'Assigned To', relation: 'wi.user' },
    comment_count: { type: 'integer', label: 'Comments', readonly: true },
    state: { type: 'selection', label: 'State', options: STATES },
    reason: { type: 'selection', label: 'Reason', options: [{ value: 'new', label: 'New' }, { value: 'approved', label: 'Approved' }, { value: 'investigated', label: 'Investigation Complete' }, { value: 'fixed', label: 'Fixed' }] },
    area: { type: 'many2one', label: 'Area', relation: 'wi.area' },
    iteration: { type: 'many2one', label: 'Iteration', relation: 'wi.iteration' },
    repro_steps: { type: 'text', label: 'Repro Steps' },
    resolved_reason: { type: 'selection', label: 'Resolved Reason', options: [{ value: 'fixed', label: 'Fixed' }, { value: 'duplicate', label: 'Duplicate' }, { value: 'as_designed', label: 'As Designed' }] },
    priority: { type: 'selection', label: 'Priority', options: [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }] },
    severity: { type: 'selection', label: 'Severity', options: [{ value: '1', label: '1 - Critical' }, { value: '2', label: '2 - High' }, { value: '3', label: '3 - Medium' }, { value: '4', label: '4 - Low' }] },
    new_comment: { type: 'text', label: 'Comment' },
    related: { type: 'many2many', label: 'Related Work', relation: 'wi.head' },
    attachments: { type: 'binary', label: 'Attachments', multiple: true },
    changed_by: { type: 'many2one', label: 'Changed By', relation: 'wi.user', readonly: true },
    changed_date: { type: 'datetime', label: 'Changed Date', readonly: true },
    following: { type: 'boolean', label: 'Following', readonly: true },
  },
  layout: {
    type: 'sheet',
    id: 'wi-bug',
    title: { field: 'title', before: [{ type: 'field', id: 't-number', field: 'number', labels: 'hidden' }] },
    head: {
      kind: { field: 'work_type', numberField: 'number', looks: { bug: { colour: '#cc293d', icon: 'bug' }, task: { colour: '#f2cb1d', icon: 'task' }, story: { colour: '#009ccc', icon: 'story' } } },
      meta: [
        { type: 'field', id: 'h-assigned', field: 'assigned_to', labels: 'hidden', placeholder: 'No one selected' },
        { type: 'link', id: 'h-comments', icon: 'chat', field: 'comment_count', words: { one: 'Comment', other: 'Comments' }, target: 'g-discussion' },
      ],
      actions: [
        { type: 'button', id: 'b-save-close', label: 'Save and Close', icon: 'save', primaryWhenChanged: true, steps: [{ do: 'save' }, { do: 'close' }], menu: [{ id: 'm-save', label: 'Save', icon: 'save', steps: [{ do: 'save' }] }] },
        { type: 'button', id: 'b-follow', label: 'Follow', pressedLabel: 'Following', icon: 'eye', pressed: 'following', action: 'follow', menuIcon: 'gear', menuLabel: 'Notification settings', menu: [{ id: 'm-follow-all', label: 'All changes', action: 'follow-all' }, { id: 'm-follow-state', label: 'State changes', action: 'follow-state' }] },
        { type: 'button', id: 'b-refresh', label: 'Refresh', icon: 'refresh', iconOnly: true, steps: [{ do: 'reload' }] },
        { type: 'button', id: 'b-undo', label: 'Revert changes', icon: 'undo', iconOnly: true, steps: [{ do: 'reset' }] },
      ],
      band: {
        tracks: ['274px', '1fr'],
        labelWidth: 76,
        children: [
          { type: 'field', id: 'f-state', field: 'state', widget: 'dot', hotkey: 'e', options: { label: true, tones: { new: 'muted', active: 'info', resolved: 'warning', closed: 'success' } } },
          { type: 'field', id: 'f-area', field: 'area', hotkey: 'a' },
          { type: 'field', id: 'f-reason', field: 'reason', readonly: true, readonlyReason: 'Set by the workflow when the State changes' },
          { type: 'field', id: 'f-iteration', field: 'iteration', hotkey: 'r' },
        ],
      },
      updated: { byField: 'changed_by', atField: 'changed_date', opens: 't-history' },
    },
    toolbar: { menu: [{ id: 'm-copy-link', label: 'Copy link', action: 'copy-link' }, { id: 'm-delete', builtin: 'delete' }] },
    children: [
      {
        type: 'tabs',
        id: 'wi-tabs',
        place: 'head',
        children: [
          {
            type: 'tab',
            id: 't-details',
            label: 'Details',
            children: [
              {
                type: 'section',
                id: 'd-columns',
                style: 'plain',
                columns: 3,
                children: [
                  { type: 'section', id: 'g-repro', title: 'Repro Steps', style: 'line', children: [{ type: 'field', id: 'f-repro', field: 'repro_steps', labels: 'hidden' }] },
                  { type: 'section', id: 'g-planning', title: 'Planning', style: 'line', children: [{ type: 'field', id: 'f-resolved-reason', field: 'resolved_reason', readonly: true, readonlyReason: 'Set when the bug is resolved' }, { type: 'field', id: 'f-priority', field: 'priority' }, { type: 'field', id: 'f-severity', field: 'severity' }] },
                  { type: 'section', id: 'g-discussion', title: 'Discussion', style: 'line', children: [{ type: 'field', id: 'f-comment', field: 'new_comment', labels: 'hidden', placeholder: 'Add a comment' }] },
                ],
              },
            ],
          },
          { type: 'tab', id: 't-history', label: 'History', icon: 'history', iconOnly: true, children: [{ type: 'text', id: 'x-history', text: 'Each change of the bug shows here.' }] },
          { type: 'tab', id: 't-links', label: 'Links', icon: 'link', iconOnly: true, count: 'related', children: [{ type: 'field', id: 'f-related', field: 'related', labels: 'hidden' }] },
          { type: 'tab', id: 't-attachments', label: 'Attachments', icon: 'attach', iconOnly: true, count: 'attachments', children: [{ type: 'field', id: 'f-files', field: 'attachments', labels: 'hidden' }] },
        ],
      },
    ],
  },
  translations: {
    ar: {
      Bug: 'خطأ',
      ID: 'الرقم',
      'Work Item Type': 'نوع العنصر',
      Task: 'مهمة',
      'User Story': 'قصة مستخدم',
      Title: 'العنوان',
      'Assigned To': 'مسند إلى',
      Comments: 'تعليقات',
      Comment: 'تعليق',
      State: 'الحالة',
      New: 'جديد',
      Active: 'نشط',
      Resolved: 'تم الحل',
      Closed: 'مغلق',
      Reason: 'السبب',
      Approved: 'معتمد',
      'Investigation Complete': 'اكتمل التحقيق',
      Fixed: 'تم الإصلاح',
      Area: 'المنطقة',
      Iteration: 'الدورة',
      'Repro Steps': 'خطوات إعادة الإنتاج',
      'Resolved Reason': 'سبب الحل',
      Duplicate: 'مكرر',
      'As Designed': 'حسب التصميم',
      'Set when the bug is resolved': 'يُضبط عند حل الخطأ',
      Priority: 'الأولوية',
      Severity: 'الخطورة',
      '1 - Critical': '1 - حرجة',
      '2 - High': '2 - عالية',
      '3 - Medium': '3 - متوسطة',
      '4 - Low': '4 - منخفضة',
      'Related Work': 'عمل مرتبط',
      Attachments: 'المرفقات',
      'Changed By': 'عدّله',
      'Changed Date': 'تاريخ التعديل',
      Following: 'تتم المتابعة',
      'No one selected': 'لم يُحدَّد أحد',
      'Save and Close': 'حفظ وإغلاق',
      Save: 'حفظ',
      Follow: 'متابعة',
      'Notification settings': 'إعدادات الإشعارات',
      'All changes': 'كل التغييرات',
      'State changes': 'تغييرات الحالة',
      Refresh: 'تحديث',
      'Revert changes': 'التراجع عن التغييرات',
      'Set by the workflow when the State changes': 'يضبطه سير العمل عند تغيّر الحالة',
      'Copy link': 'نسخ الرابط',
      Details: 'التفاصيل',
      Planning: 'التخطيط',
      Discussion: 'النقاش',
      'Add a comment': 'أضف تعليقًا',
      History: 'السجل',
      'Each change of the bug shows here.': 'يظهر هنا كل تغيير على الخطأ.',
      Links: 'الروابط',
    },
    de: {
      Bug: 'Fehler',
      ID: 'ID',
      'Work Item Type': 'Arbeitselementtyp',
      Task: 'Aufgabe',
      'User Story': 'User Story',
      Title: 'Titel',
      'Assigned To': 'Zugewiesen an',
      Comments: 'Kommentare',
      Comment: 'Kommentar',
      State: 'Status',
      New: 'Neu',
      Active: 'Aktiv',
      Resolved: 'Gelöst',
      Closed: 'Geschlossen',
      Reason: 'Grund',
      Approved: 'Genehmigt',
      'Investigation Complete': 'Untersuchung abgeschlossen',
      Fixed: 'Behoben',
      Area: 'Bereich',
      Iteration: 'Iteration',
      'Repro Steps': 'Schritte zum Reproduzieren',
      'Resolved Reason': 'Lösungsgrund',
      Duplicate: 'Duplikat',
      'As Designed': 'Wie vorgesehen',
      'Set when the bug is resolved': 'Wird gesetzt, wenn der Fehler gelöst ist',
      Priority: 'Priorität',
      Severity: 'Schweregrad',
      '1 - Critical': '1 - Kritisch',
      '2 - High': '2 - Hoch',
      '3 - Medium': '3 - Mittel',
      '4 - Low': '4 - Niedrig',
      'Related Work': 'Verknüpfte Arbeit',
      Attachments: 'Anhänge',
      'Changed By': 'Geändert von',
      'Changed Date': 'Änderungsdatum',
      Following: 'Wird verfolgt',
      'No one selected': 'Niemand ausgewählt',
      'Save and Close': 'Speichern und schließen',
      Save: 'Speichern',
      Follow: 'Verfolgen',
      'Notification settings': 'Benachrichtigungseinstellungen',
      'All changes': 'Alle Änderungen',
      'State changes': 'Statusänderungen',
      Refresh: 'Aktualisieren',
      'Revert changes': 'Änderungen zurücksetzen',
      'Set by the workflow when the State changes': 'Wird vom Workflow gesetzt, wenn sich der Status ändert',
      'Copy link': 'Link kopieren',
      Details: 'Details',
      Planning: 'Planung',
      Discussion: 'Diskussion',
      'Add a comment': 'Kommentar hinzufügen',
      History: 'Verlauf',
      'Each change of the bug shows here.': 'Jede Änderung am Fehler erscheint hier.',
      Links: 'Links',
    },
    fr: {
      Bug: 'Bogue',
      ID: 'ID',
      'Work Item Type': 'Type d’élément',
      Task: 'Tâche',
      'User Story': 'Récit utilisateur',
      Title: 'Titre',
      'Assigned To': 'Attribué à',
      Comments: 'Commentaires',
      Comment: 'Commentaire',
      State: 'État',
      New: 'Nouveau',
      Active: 'Actif',
      Resolved: 'Résolu',
      Closed: 'Fermé',
      Reason: 'Raison',
      Approved: 'Approuvé',
      'Investigation Complete': 'Enquête terminée',
      Fixed: 'Corrigé',
      Area: 'Zone',
      Iteration: 'Itération',
      'Repro Steps': 'Étapes de reproduction',
      'Resolved Reason': 'Raison de résolution',
      Duplicate: 'Doublon',
      'As Designed': 'Conforme à la conception',
      'Set when the bug is resolved': 'Défini quand le bogue est résolu',
      Priority: 'Priorité',
      Severity: 'Gravité',
      '1 - Critical': '1 - Critique',
      '2 - High': '2 - Élevée',
      '3 - Medium': '3 - Moyenne',
      '4 - Low': '4 - Faible',
      'Related Work': 'Travail lié',
      Attachments: 'Pièces jointes',
      'Changed By': 'Modifié par',
      'Changed Date': 'Date de modification',
      Following: 'Suivi',
      'No one selected': 'Personne n’est choisi',
      'Save and Close': 'Enregistrer et fermer',
      Save: 'Enregistrer',
      Follow: 'Suivre',
      'Notification settings': 'Paramètres de notification',
      'All changes': 'Toutes les modifications',
      'State changes': 'Changements d’état',
      Refresh: 'Actualiser',
      'Revert changes': 'Annuler les modifications',
      'Set by the workflow when the State changes': 'Défini par le flux de travail quand l’état change',
      'Copy link': 'Copier le lien',
      Details: 'Détails',
      Planning: 'Planification',
      Discussion: 'Discussion',
      'Add a comment': 'Ajouter un commentaire',
      History: 'Historique',
      'Each change of the bug shows here.': 'Chaque modification du bogue s’affiche ici.',
      Links: 'Liens',
    },
  },
};

/** The bug and the records its links point to: invented. */
export const WI_HEAD_RECORDS: Record<string, Record<string, Values>> = {
  'wi.head': {
    1: {
      number: 4127,
      work_type: 'bug',
      title: 'Stock count shows 12 after releasing 3 of 15 items to order SO-2088',
      assigned_to: null,
      comment_count: 1,
      state: 'active',
      reason: 'approved',
      area: { id: 1, label: 'Portal' },
      iteration: { id: 5, label: 'Portal\\Sprint 14' },
      repro_steps: 'Release 3 of the 15 items on order SO-2088 from the warehouse, then open the product: its stock count shows 12, not 15.',
      priority: '1',
      severity: '3',
      new_comment: null,
      related: [],
      attachments: [{ name: 'stock-count.png', type: 'image/png', size: 48211 }],
      changed_by: { id: 1, label: 'Mona Adel' },
      changed_date: '2026-08-25T09:40:00',
      following: false,
    },
    2: { number: 4128, work_type: 'task', title: 'Count stock again after a partial release', state: 'new', related: [], attachments: [] },
  },
  'wi.user': { 1: { name: 'Mona Adel' }, 2: { name: 'Karim Fathy' }, 3: { name: 'Salma Nabil' }, 4: { name: 'Omar Hany' } },
  // Its areas and iterations are the work tracker's, with their paths (shared/wi-picker.ts): one table of each.
};

/** The app's answers to the head's buttons: Follow writes whether the bug is followed as the server keeps it, never a change to save. */
export function wiHeadAction(request: ActionRequest): ActionResult | undefined {
  if (request.action === 'follow') return { stored: { following: request.values['following'] !== true } };
  if (request.action === 'follow-all' || request.action === 'follow-state') return { stored: { following: true }, say: request.action === 'follow-all' ? 'Following every change' : 'Following state changes' };
  if (request.action === 'copy-link') return { say: 'Link copied' };
  return undefined;
}

const FONT = '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Helvetica, Ubuntu, Arial, sans-serif';

/**
 * A work tracker's own colours, as the host gives them (measured, §2.2 of the
 * replica's plan), light and dark: the popup wears them. Muted words are a
 * shade darker than the measured #7e7e7e, which reads at less than 4.5:1 on
 * the band: Fieldia keeps every word readable (WCAG AA).
 */
export function wiHeadTokens(dark: boolean): Tokens {
  return dark
    ? { text: '#f3f2f1', muted: '#a19f9d', page: '#252423', surface: '#252423', fill: '#3b3a39', border: '#3b3a39', 'border-strong': '#8a8886', edge: '#8a8886', accent: '#2899f5', 'accent-text': '#0a0a0a', 'accent-soft': '#0e2d4a', focus: '#2899f5', 'focus-ring': 'none', radius: '4px', 'control-radius': '2px', font: FONT }
    : { text: '#1a1a1a', muted: '#707070', page: '#ffffff', surface: '#ffffff', fill: '#f2f2f2', border: '#eaeaea', 'border-strong': '#7e7e7e', accent: '#0078d4', 'accent-text': '#ffffff', 'accent-soft': '#deecf9', focus: '#0078d4', 'focus-ring': 'none', radius: '4px', 'control-radius': '2px', font: FONT };
}
